/**
 * `clientWork` — the two blocks session 41 puts at the top of the client dashboard:
 * **what we are working on**, and **what we delivered recently**.
 *
 * Ferit's own words, 18 September 2026: "the actual primary goal is for them to see
 * that we are working on their stuff." Nothing on any client page said so — the
 * dashboard opened on last month's report figures, and the only forward-looking
 * panel was `clientUpcoming`, which is a calendar of dates rather than a statement
 * about work in flight.
 *
 * It is its own read model rather than three more fields computed inside
 * `client-dashboard.ts`, because that file composes and does not compute: its header
 * says "Nothing is computed here", and a stage, a committed date and a last-update
 * date are all derived. `readmodels/client-work.ts` is where the deriving happens,
 * once, and the dashboard composes it exactly as it composes `clientUpcoming`.
 *
 * **Counts, not sentences.** `pieces` is `{ published, planned }` rather than the
 * string "3 of 12 pieces published", for the reason `files-client.ts`'s
 * `FilesEarlierMonth.items` gives: pluralising a noun is rendering, and rendering is
 * the SPA's (`.docs/00-index.md` §5). The same rule puts the month name out of reach
 * here — a content plan has no title column, so its row carries `period` and the
 * page writes "September content plan" through `useFormat`.
 */
import { z } from 'zod';
import { DateOnlySchema, PeriodSchema } from '../primitives.js';
/**
 * The three kinds of thing a client has in flight.
 *
 * A **project** is a `work_order` of kind `project` in `in_progress` — approved,
 * started, not yet delivered. A **content plan** is the live retainer's plan for the
 * current period, which is the only one the studio is producing against. A **task**
 * is a row on the studio's own board filed against this client, in a queue
 * `CLIENT_HIDDEN_QUEUES` does not hide, that is neither done nor cancelled.
 *
 * The third arrived in session 43, and it is what makes this block true for the
 * studio that actually exists. The portal was wired end to end to the contractual
 * workflow — work order, milestone, deliverable, periodised file, issued report —
 * and the studio has never once performed it: on 22 September 2026 production held
 * 56 tasks and zero work orders, so every client read an almost empty dashboard
 * while the studio was demonstrably working. The operational record was the task
 * board and no client surface read it.
 *
 * **It is a third `kind` rather than a fourth field or a second read model**,
 * because `ClientWorkItemSchema` already says everything a task row needs to say:
 * `stage` is a free string meaning "where it stands", the sort puts a null date
 * last, and the page's own meta line leaves out the parts that are absent. The
 * shape below therefore does not change, and that is also the boundary: it is a
 * `strictObject`, and every field `tasks/service.ts` `view()` publishes and a client
 * may not read — `instructions`, `acceptance`, `blocked_reason`, `outputs` (which
 * carry prepared `$` action payloads), `money_*`, `assignee_*`, `lease_expires_at` —
 * is not a field of it. A leak is a type error at the mapper and a zod failure over
 * the recorded world, rather than something a reviewer has to notice.
 *
 * Deliberately not a fourth kind for extras: an approved extra is delivered as a
 * `deliverable` and appears under *Delivered recently* the day it lands, and a row
 * saying "we are working on three reels" that never changes until it is done is a
 * line that states nothing the client can act on.
 */
export const CLIENT_WORK_KINDS = ['project', 'content_plan', 'task'];
export const ClientWorkItemSchema = z
    .strictObject({
    kind: z.enum(CLIENT_WORK_KINDS).describe('Which of the three shapes this row is.'),
    ref: z
        .string()
        .describe('`WO-2026-008` for a project, `PLAN-2026-09` for a content plan, `TSK-0042` for a ' +
        'task. Always present, and unique across the three.'),
    title: z
        .string()
        .describe('The work order’s own title, or the task’s own. **Empty for a content plan**, which ' +
        'has no title column — that row is named by its `period` instead, and the page ' +
        'writes the month.'),
    period: PeriodSchema.nullable().describe('The content plan’s month. Null on a project and on a task, neither of which is a ' +
        'monthly thing.'),
    stage: z
        .string()
        .describe('Where a project stands: the title of the first milestone not yet delivered. Empty ' +
        'when the project has no schedule at all, which is a real state and not an error — ' +
        'the row then states its title and no stage. Always empty for a content plan, whose ' +
        '`pieces` says where it stands instead. On a task it is one of three words — ' +
        'Queued, In progress, On hold (`CLIENT_TASK_STAGES`) — which is a narrowing of the ' +
        'seven studio states and is therefore decided on the server.'),
    pieces: z
        .strictObject({
        published: z.int().nonnegative().describe('Items already published.'),
        planned: z.int().nonnegative().describe('Items the plan holds, published ones included.'),
    })
        .nullable()
        .describe('A content plan’s progress. Null on a project and on a task. `published` is 0 on a ' +
        'plan nobody has delivered against yet, which is the row reading "0 of 12" rather ' +
        'than reading nothing.'),
    next_on: DateOnlySchema.nullable().describe('The next date the studio has committed to — the undelivered milestone’s `due_on`, the ' +
        'plan’s next unpublished item’s day, or the task’s `due_at` as a day. Null when ' +
        'nothing is dated, and a null is a line the page leaves out rather than a placeholder ' +
        '(session 41, T4).'),
    last_update: DateOnlySchema.nullable().describe('The last day anything happened on this piece of work, in the reader’s zone: the latest ' +
        'of a milestone’s `delivered_at`, a deliverable’s `delivered_on` and a studio comment’s ' +
        '`at`, or a task’s own `updated_at`. Null when nothing has happened since it started. ' +
        'A **date**, never "N days ago" — the page may not compute and the client reads ' +
        'absolute dates everywhere else.'),
})
    .describe('One piece of work in flight, as the dashboard’s "What we are working on" states it.');
/** One thing that landed: a milestone, or a deliverable against the retainer. */
export const ClientDeliveredItemSchema = z
    .strictObject({
    title: z
        .string()
        .describe('What it was — a milestone’s title qualified by its project, a deliverable’s own ' +
        'title, or a finished task’s. Never empty: a deliverable with no title falls back ' +
        'to its service code, because a row with no name is a row nobody can read.'),
    on: DateOnlySchema.describe('The day it was delivered.'),
})
    .describe('One delivered thing, named and dated.');
export const ClientRecentlyDeliveredSchema = z
    .strictObject({
    period: PeriodSchema.describe('The month counted — the billing period the dashboard is in.'),
    count: z
        .int()
        .nonnegative()
        .describe('Everything delivered in that month: deliverables, milestones and finished tasks.'),
    items: z
        .array(ClientDeliveredItemSchema)
        .describe('The three latest by day, newest first. Fewer when the month holds fewer.'),
})
    .describe('"Delivered recently" — a count, three names, and one link to Files for the rest.');
export const ClientWorkSchema = z
    .strictObject({
    work_in_progress: z
        .array(ClientWorkItemSchema)
        .describe('Everything in flight, the nearest committed date first. Empty is one line.'),
    recently_delivered: ClientRecentlyDeliveredSchema,
})
    .describe('What the studio is doing for this client now, and what it finished this month.');
/**
 * `GET /api/clients/me/work` — the whole of what is in flight, a page at a time.
 *
 * The dashboard's block names five and refers here; this is the destination, and it
 * exists because `.docs/24`'s scale rule is that a new list is bounded and *refers
 * onwards*. For a project the referring was always the Projects page; for a task
 * there was nowhere to refer to, and one production client already has twenty-four.
 *
 * The rows are `ClientWorkItemSchema`'s, which is what keeps one projection for two
 * readers: the dashboard block and this page name the same work with the same words,
 * because they are the same mapper rather than two that agree today.
 */
export const ClientWorkPageSchema = z
    .strictObject({
    items: z
        .array(ClientWorkItemSchema)
        .describe('This page of the work in flight, the nearest committed date first.'),
    next_cursor: z
        .string()
        .nullable()
        .describe('Pass it back as `cursor` for the next page. Null on the last one.'),
})
    .describe('Everything the studio has in flight for this client, paged.');
//# sourceMappingURL=work.js.map